import React, { useState, useEffect, useRef } from 'react';
import { Container, Paper, Typography, Button, Box, CircularProgress, Alert, LinearProgress } from '@mui/material';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import useManageCompositeJob from './useManageCompositeJob';
import { toast } from 'react-toastify';
import io from 'socket.io-client';

const CompositeJob = () => {
  const [shipDateId, setShipDateId] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [completedComposites, setCompletedComposites] = useState(0);
  const [totalComposites, setTotalComposites] = useState(0);
  const socketRef = useRef(null);
  const jobIdRef = useRef(null);
  const shipDateIdRef = useRef(null);
  const getCompositeJobRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const {
    compositeJob,
    isLoading,
    error,
    getCompositeJobByShipDate,
    generateComposite,
    downloadCompositeZip
  } = useManageCompositeJob();

  // Update refs when values change
  useEffect(() => {
    jobIdRef.current = compositeJob?.jobId;
  }, [compositeJob?.jobId]);

  useEffect(() => {
    shipDateIdRef.current = shipDateId;
  }, [shipDateId]);

  useEffect(() => {
    getCompositeJobRef.current = getCompositeJobByShipDate;
  }, [getCompositeJobByShipDate]);

  // Check status on page load and when shipDateId changes
  useEffect(() => {
    if (shipDateId) {
      getCompositeJobByShipDate(shipDateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipDateId]);

  // Socket connection for progress updates
  useEffect(() => {
    // withCredentials so the admin_token cookie rides the handshake: every
    // namespace including the root one is admin-only now (B5), and without
    // it the connection is refused and no composite-progress/-complete
    // event ever arrives.
    const socket = io(BACKEND_URL, { withCredentials: true });
    socketRef.current = socket;

    socket.on('connect', () => {});

    socket.on('disconnect', () => {});

    // Listen for progress updates
    const handleProgress = (data) => {
      // If jobIdRef is not set yet, store the incoming jobId
      if (!jobIdRef.current && data.jobId) {
        jobIdRef.current = data.jobId;
      }
      
      // Accept progress if jobId matches
      if (data.jobId && (jobIdRef.current === data.jobId)) {
        setProgress(data.progress || 0);
        setCompletedComposites(data.completedComposites || 0);
        setTotalComposites(data.totalComposites || 0);
        setIsGenerating(true);
      }
    };

    // Listen for completion
    const handleComplete = (data) => {
      // If jobIdRef is not set yet, store the incoming jobId
      if (!jobIdRef.current && data.jobId) {
        jobIdRef.current = data.jobId;
      }
      
      // Accept completion if jobId matches
      if (data.jobId && (jobIdRef.current === data.jobId)) {
        setProgress(100);
        setIsGenerating(false);
        toast.success('Composite job completed!');
        // Refresh job status to show download button
        setTimeout(() => {
          const currentShipDateId = shipDateIdRef.current;
          const getJobFn = getCompositeJobRef.current;
          if (currentShipDateId && getJobFn) {
            getJobFn(currentShipDateId);
          }
        }, 1500);
      }
    };

    socket.on('composite-progress', handleProgress);
    socket.on('composite-complete', handleComplete);

    return () => {
      socket.off('composite-progress', handleProgress);
      socket.off('composite-complete', handleComplete);
      socket.disconnect();
    };
  }, [BACKEND_URL]);

  const handleGenerate = async () => {
    if (!shipDateId) {
      toast.warning('Please select a ship date');
      return;
    }

    // Clear the previous job ID so the first socket event from the new job
    // can self-register via the "!jobIdRef.current" guard in handleProgress/handleComplete.
    // Without this, events for the new job are rejected because jobIdRef still
    // holds the old job's ID.
    jobIdRef.current = null;

    setIsGenerating(true);
    setProgress(0);
    setCompletedComposites(0);
    setTotalComposites(0);
    const success = await generateComposite(shipDateId);
    if (success) {
      toast.success('Generate composite job started');
      // Wait a moment then check status to get jobId
      setTimeout(() => {
        getCompositeJobByShipDate(shipDateId);
      }, 1000);
    } else {
      toast.error(error || 'Failed to generate composite');
      setIsGenerating(false);
      setProgress(0);
    }
  };

  const handleDownload = async () => {
    if (!shipDateId) {
      toast.warning('Please select a ship date');
      return;
    }

    setIsDownloading(true);
    const success = await downloadCompositeZip(shipDateId);
    if (success) {
      toast.success('Download started');
    } else {
      toast.error(error || 'Failed to download');
    }
    setIsDownloading(false);
  };

  // Check if job exists and is completed or downloaded
  const hasJob = compositeJob?.exists && (compositeJob?.status === 'completed' || compositeJob?.status === 'downloaded');

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Composite Job Management
        </Typography>

        <Box sx={{ mt: 3, mb: 4 }}>
          <ShipDateDropdown
            handleDataChange={(e) => setShipDateId(e.target.value)}
            includeDefault={true}
          />
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && shipDateId && (
          <Box sx={{ mt: 3 }}>
            {/* Generate Button */}
            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleGenerate}
                disabled={isGenerating}
                sx={{ minWidth: 200 }}
              >
                {isGenerating ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Generating...
                  </>
                ) : (
                  'Generate Composite'
                )}
              </Button>
            </Box>

            {/* Image Count Info - Always show when compositeJob data is available */}
            {compositeJob && compositeJob.imageCount !== undefined && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Found {compositeJob.imageCount} image{compositeJob.imageCount !== 1 ? 's' : ''} for this ship date
              </Alert>
            )}

            {/* Progress Bar - Show when generating or when progress is updating */}
            {(isGenerating || progress > 0) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Progress: {progress}%
                  {totalComposites > 0 && ` (${completedComposites}/${totalComposites} composites)`}
                </Typography>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            )}

            {/* Status Info - Only show if job exists */}
            {compositeJob?.exists && (
              <Alert 
                severity={(compositeJob.status === 'completed' || compositeJob.status === 'downloaded') ? 'success' : 'info'} 
                sx={{ mb: 3 }}
              >
                Job status: {compositeJob.status || 'Unknown'}
                {compositeJob.totalComposites && ` | Total composites: ${compositeJob.totalComposites}`}
                {compositeJob.completedComposites !== undefined && ` | Completed: ${compositeJob.completedComposites}`}
              </Alert>
            )}

            {/* Download Button - Show if job exists and is completed or downloaded */}
            {compositeJob?.exists && (compositeJob?.status === 'completed' || compositeJob?.status === 'downloaded') && compositeJob?.jobId && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={handleDownload}
                  disabled={isDownloading}
                  sx={{ minWidth: 200 }}
                >
                  {isDownloading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Downloading...
                    </>
                  ) : (
                    'Download Composite'
                  )}
                </Button>
              </Box>
            )}

            {/* Only show warning if no job exists */}
            {(!compositeJob || !compositeJob.exists) && shipDateId && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No composite jobs found for this ship date. Click "Generate Composite" to create one.
              </Alert>
            )}
          </Box>
        )}

        {!shipDateId && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Please select a ship date to view or generate composite jobs.
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default CompositeJob;

